import Primary from "../layouts/Primary";
import TwoThirds from "../layouts/TwoThirds";
import OneThird from "../layouts/OneThird";
import {useAsset} from "../../AssetHelper";

import './Match.scss';
import {useTimer} from "../../hooks";
import {ReactElement, ReactNode, useContext, useEffect} from "react";
import play from "../../sounds";
import FullScore, {ScoreType} from "../components/decode/FullScore";
import Score from "../components/decode/Score";
import {DisplayConfig} from "../Config";
import {
    DisplayCommand,
    MatchAlliance,
    MatchTeam,
    ScoreUpdate,
    ShowMatch, TournamentLevel,
} from "../../javaTypes";
import {t} from "../../i18n";
import DisplayParams, {DisplayParamsType} from "../DisplayParams";
import {renderTime} from "../../utils";
import CmpBar, {truncateMatchNameForCmpBar} from "../layouts/CmpBar";
import PrimaryMatch from "../layouts/PrimaryMatch";
import FormattedSeed from "../components/FormattedSeed";
import EventLogo from "../components/EventLogo";
import {RandomizationArtifacts} from "./Randomization";
import { Temporal } from "temporal-polyfill";

/**
 * Total score boxes
 */

function TotalScore({color, right, short, together, seed, score, otherScore, isAuto, hasWin} : {
    color: 'red' | 'blue' | 'orange',
    right?: boolean,
    short?: boolean,
    together?: boolean,
    seed?: string,
    score: Partial<ScoreType>,
    otherScore: Partial<ScoreType>,
    isAuto?: boolean,
    hasWin?: boolean
}) {
    // TODO plumb teleopToSubtractFromAuto and teleopToSubtractFromInMatch from backend into here
    let total = score.preFoulTotal === undefined ? 0 :
        score.preFoulTotal + (otherScore.foulPointsCommitted || 0) - (isAuto ? 0 : 0) -
            (score.teleopBasePoints || 0) - (score.teleopDepotPoints || 0);
    const displayColor = color[0].toUpperCase() + color.slice(1);
    return <div className={`totalScoreWrapper ${right ? "right" : "left"} ${short ? "short" : ""} ${together ? "together" : ""}`}>
        {hasWin ? <div className="winTrophy"><i className={`fa fa-star`}></i></div> : null}
        <div id={`${color}Seed`} className={`allianceSeed `}>
            {seed ? <FormattedSeed seed={seed} semiCompact /> : displayColor}
        </div>
        <div id={`${color}Total`}
                className={`scoreTotal ${short ? "short" : ""} ${total > 999 ? "four-digit" : ""}`}>
            {total}
        </div>
    </div>
}

/**
 *  Set of team number indicators
 */
function WingSet({color, alliance, isParent, hflip, wingSpan, teamCount, showSeed} : {
    color: 'red' | 'blue',
    alliance: MatchAlliance<MatchTeam>,
    isParent?: boolean,
    hflip?: boolean,
    wingSpan?: 'jumbo' | 'standard' | 'overlay' | 'compact',
    teamCount: number,
    showSeed?: boolean
}) {
    if(!alliance) return null;
    let teams = !alliance.teams ? [] : [...alliance.teams];
    let side: "right" | "left" = hflip ? (color === "red" ? "right" : "left") : (color === "red" ? "left" : "right");
    let items = teams.map((team, i) =>
        team.number === "" ? null : <div key={i} id={`${color}${i + 1}`} className={`teamNumber team${i + 1} ${color}Number ${team.carriesCard ? "carriesCard" : ""}`}>{team.number}</div>
    );
    if(showSeed && alliance.seed) {
        const seedElem = <span className={"seed"}>
            <FormattedSeed seed={alliance.seed} compact />
        </span>;
        items.splice(side == "left" ? 1 : 0, 0, seedElem)
    }
    return <div id={`${color}Numbers`} className={`teamNumbers ${side} number-count-${teams.length} ${wingSpan} ${isParent ? "parent" : ""}`}>
        {items}
    </div>
}

function isEmptyCommand(command: DisplayCommand<any>) : command is DisplayCommand<{ _empty: any }> {
    return '_empty' in command.params
}

const fallbackTiming = {
    auto: Temporal.Duration.from({seconds: 30}),
    transition: Temporal.Duration.from({seconds: 8}),
    teleop: Temporal.Duration.from({seconds: 120}),
    endgameWarning: Temporal.Duration.from({seconds: 20}),
    total: Temporal.Duration.from({seconds: 158})
}

export default function Match({config, command, nowCallback, scoreUpdate, keyPanel, audienceKey, timerOnly} : {
    config: DisplayConfig,
    command: DisplayCommand<ShowMatch> | DisplayCommand<{ _empty: any }>,
    nowCallback: () => number,
    scoreUpdate?: DisplayCommand<ScoreUpdate>,
    keyPanel?: 'auto' | 'teleop' | 'shared' | 'all',
    ref?: () => void,
    audienceKey?: ReactNode,
    timerOnly?: boolean
}) {
    const displayParams = useContext(DisplayParams);
    const style = timerOnly ? 'timerOnly' : displayParams.style
    const isEmpty = isEmptyCommand(command);
    const isSingleTeam = !isEmpty && command.params.singleTeam;
    const isPlayoff = !isEmpty && command.params.tournamentLevel == TournamentLevel.PLAYOFF;
    const timing = displayParams.matchTiming ?? fallbackTiming;
    const { remaining, running } = useTimer({
        startAt: command.type === 'START_MATCH' ? command.ts : null,
        duration: command.type === 'ABORT_MATCH' ? 0 : (timing.total.total('seconds')) + (isSingleTeam ? 3 : 0),
        nowCallback: nowCallback,
        sounds: config.mute ? {} : (
            {
                [timing.total.add({seconds: 3}).total('seconds')]: 'countdown',
                [timing.total.total('seconds')]: 'matchStart',
                [timing.teleop.add(timing.transition).total('seconds')]: 'autoEnd',
                [timing.teleop.add(timing.transition).subtract({seconds: 2}).total('seconds')]: 'controllers',
                [timing.teleop.add({seconds: 3}).total('seconds')]: 'countdown',
                [timing.teleop.total('seconds')]: 'startTeleop',
                [timing.endgameWarning.total('seconds')]: 'endgameWarning',
                [0]: 'matchEnd'
            })
    })
    useEffect(() => {
      if(command.type === 'ABORT_MATCH' && nowCallback() - command.ts < 1000) {
          play('abortMatch')
      }
    }, [command.ts])
    let displayTimeRemaining = remaining;
    let icon : 'robot' | 'gamepad' | 'hand' | null = null;
    let period : 'auto' | 'teleop' | 'auto_transition' | 'endgame' | 'review' | null = null;
    let ScoreElement = Score;
    if(running) {
        if (remaining > timing.total.total('seconds')) {
            displayTimeRemaining = timing.total.subtract(timing.transition).total('seconds');
        } else if (remaining > timing.teleop.add(timing.transition).total('seconds')) {
            displayTimeRemaining = remaining - timing.transition.total('seconds');
            period = 'auto';
            icon = 'robot';
            ScoreElement = Score;
        } else if (remaining > timing.teleop.total('seconds')) {
            displayTimeRemaining = remaining - timing.teleop.total('seconds');
            period = 'auto_transition';
            icon = 'hand';
            ScoreElement = Score;
        } else if (remaining > timing.endgameWarning.total('seconds')) {
            period = 'teleop';
            icon = 'gamepad';
        } else if (remaining > 0) {
            period = 'endgame';
            icon = 'gamepad';
        }
    } else if(command.type === 'ABORT_MATCH') {
        displayTimeRemaining = 0;
    } else if(command.type === 'SHOW_MATCH') {
        displayTimeRemaining = timing.total.subtract(timing.transition).total('seconds')
        ScoreElement = Score;
    } else if (remaining === 0) {
        period = 'review';
    }
    const vflip = config.scoringBarLocation === 'top';
    const hflip = config.allianceOrientation === 'flipped';

    /*
        Layouts:
        AD (scoring elements in 2/3, medium-timer in 1/3 ) [vflip & hflip]
        FD (big timer in 2/3, scoring elements in 1/3) [vflip & hflip]
        overlay (scoring elements in 1/3, chroma rest) [vflip & hflip]
        no-live (Primary w/ big timer with wings) [hflip]
        no-live overlay (medium timer in 1/3, chroma rest) [vflip & hflip]
     */
    if (!isEmpty && displayParams.liveDisabled && !config.overlay) {
        let wings = (["red", "blue"] as const).map(a => <WingSet key={a} color={a} alliance={command.params[a]} hflip={hflip} wingSpan="jumbo" teamCount={Math.max(command.params.red.teams.length, command.params.blue.teams.length)}/>);
        return <Primary>
            <div className="timer">
                {renderTime(displayTimeRemaining)}
                {icon ? <i className={`fas fa-${icon}`} /> : null }
            </div>
            {wings}
        </Primary>
    } else if(style === 'timerOnly') {
        return <Primary>
            <div className="timer">
                {renderTime(displayTimeRemaining)}
                {icon ? <i className={`fas fa-${icon}`} /> : null }
            </div>
        </Primary>
    }
    let twoThirds;
    const oneThirdTimer = (isSingleTeam ? config.overlay : !config.fieldStyleTimer) || displayParams.liveDisabled; // if the total scores are touching
    const redScore = scoreUpdate ? (isSingleTeam ? scoreUpdate.params.redScores : scoreUpdate.params.redScores) : {}
    const blueScore = scoreUpdate ? (isSingleTeam ? scoreUpdate.params.redScores : scoreUpdate.params.blueScores) : {}
    let oneThirdScores = false;
    const showHrReviewBanner = period === 'review' && scoreUpdate?.params.hrReview;
    if (config.type == "cmpBar") {
        const redSection = <div className={"matchInfo red"}>
            <TotalScore color={"red"} score={redScore} otherScore={blueScore} isAuto={period?.startsWith('auto')} />
            {isEmpty ? null : <WingSet color={"red"} alliance={command.params.red} showSeed isParent={displayParams.isDualDivisionParent} teamCount={Math.max(command.params.red.teams.length, command.params.blue.teams.length)}/>}
        </div>
        const blueSection = <div className={"matchInfo blue"}>
            <TotalScore color={"blue"} score={blueScore} otherScore={redScore} isAuto={period?.startsWith('auto')} />
            {isEmpty ? null : <WingSet color={"blue"} alliance={command.params.blue} showSeed isParent={displayParams.isDualDivisionParent} teamCount={Math.max(command.params.red.teams.length, command.params.blue.teams.length)}/>}
        </div>
        return <CmpBar timer={renderTime(displayTimeRemaining)} pageTitle={isEmpty ? "" : <><EventLogo logo={displayParams.matchPrefixLogo} /> {truncateMatchNameForCmpBar(command.params.matchName)}</>}>
            <div className="matchFull">
                {hflip ? [blueSection, redSection] : [redSection, blueSection]}
            </div>
        </CmpBar>
    } else if (config.overlay) {
        twoThirds = <div className={`chroma ${vflip ? "vflip" : ""}`} style={{backgroundColor: `${config.overlayColor}`}}/>;
        if(!displayParams.liveDisabled) {
            oneThirdScores = true;
        }
    } else if (config.fieldStyleTimer) {
        twoThirds = <TwoThirds vflip={vflip}>
            <div id="fieldHeader">{isSingleTeam ? t`Match Timer` : (!isEmpty && command.params.field > 0 ? t`Field ${command.params.field}` : '')}</div>
            <div className="timer">
                {renderTime(displayTimeRemaining)}
                {icon ? <i className={`fas fa-${icon}`} /> : <i className={`fas`}>&nbsp;</i> }
                {scoreUpdate ? <RandomizationArtifacts number={scoreUpdate.params.rand as 1 | 2 | 3} /> : null}
            </div>
            {showHrReviewBanner ? <div className={"reviewBanner"}>{t`Match Under Review`}</div> : null}
        </TwoThirds>;
        oneThirdScores = !displayParams.liveDisabled;
    } else {
        if (isSingleTeam) {
            twoThirds = <TwoThirds vflip={vflip}>
                <FullScore color={"orange"} score={redScore} otherScore={{}} isSingleTeam
                           currentPeriod={period} keyPanel={keyPanel} isPlayoff={isPlayoff} />
                <div className="smallTimer">
                    <div className="timer">
                        {renderTime(displayTimeRemaining)}
                        {icon ? <i className={`fas fa-${icon}`} /> : null }
                    </div>
                </div>
            </TwoThirds>
        } else {
            const redFullScores = <FullScore color={"red"} score={redScore} otherScore={blueScore}
                                             currentPeriod={period} right={hflip} keyPanel={keyPanel} hrReview={showHrReviewBanner} isPlayoff={isPlayoff} />;
            const blueFullScores = <FullScore color={"blue"} score={blueScore} otherScore={redScore}
                                              currentPeriod={period} right={!hflip} keyPanel={keyPanel} hrReview={showHrReviewBanner} isPlayoff={isPlayoff} />;
            twoThirds = <TwoThirds vflip={vflip}>
                <div className={"d-flex h-100"}>
                    {hflip ? [blueFullScores, redFullScores] : [redFullScores, blueFullScores]}
                </div>
            </TwoThirds>;
        }
    }


    return <div className={`h-100 w-100 ${isSingleTeam ? 'singleTeam' : ''} matchPage`}>
        {twoThirds}
        <OneThird matchName={isEmpty ? '' : <><EventLogo logo={displayParams.matchPrefixLogo} />&nbsp;{command.params.matchName}</>} vflip={vflip}>
            {!isEmpty ? (isSingleTeam ? [] : ['red', 'blue'] as const).map(a => <WingSet key={a} color={a} alliance={command.params[a]} hflip={hflip} wingSpan={!displayParams.liveDisabled ? (config.overlay || !config.fieldStyleTimer ? "overlay" : "standard") : "compact"} teamCount={Math.max(command.params.red.teams.length, command.params.blue.teams.length)}/>) : null}
            {!displayParams.liveDisabled ?
                <>
                    <div className={`scoreBackground left ${isSingleTeam ? "orange" : (hflip ? "blue" : "red")}`}>
                        <svg width={oneThirdTimer ? "862px" : "960px"} height="180px">
                            <polygon points={oneThirdTimer ? "0,0 862,0 862,180 637,180 637,22 0,22" : "0,0 960,0 960,180 735,180 735,22 0,22"} />
                        </svg>
                    </div>
                    <div className={`scoreBackground right ${isSingleTeam ? "orange" : (hflip ? "red" : "blue")}`}>
                        <svg width={oneThirdTimer ? "862px" : "960px"} height="180px">
                            <polygon points={oneThirdTimer ? "0,0 862,0 862,22 225,22 225,180 0,180" : "0,0 960,0 960,22 225,22 225,180 0,180"} />
                        </svg>
                    </div>
                </> : null}
            {!displayParams.liveDisabled && !isSingleTeam ? <>
                <TotalScore color="red" right={hflip} seed={isEmpty ? undefined : command.params.red.seed} short={config.overlay} together={!oneThirdTimer} score={redScore} otherScore={blueScore} isAuto={period?.startsWith('auto')} hasWin={isEmpty ? false : command.params.red.wins > 0 && command.params.red.teams[0].ranking == 0}/>
                <TotalScore color="blue" right={!hflip} seed={isEmpty ? undefined : command.params.blue.seed} short={config.overlay} together={!oneThirdTimer} score={blueScore} otherScore={redScore} isAuto={period?.startsWith('auto')} hasWin={isEmpty ? false : command.params.blue.wins > 0 && command.params.blue.teams[0].ranking == 0}/>
            </> : null}
            {!displayParams.liveDisabled && isSingleTeam ? <>
                <div id={`orangeTeamNumber`}
                     className={`scoreTotal left ${config.overlay ? "short" : ""} ${oneThirdTimer ? '' : 'together'}`}>
                    <span>{t`Team`}</span>
                    {command.params.red.teams[0].number}
                </div>
                <TotalScore color="orange" right short={config.overlay} together={!oneThirdTimer} score={redScore} otherScore={blueScore} isAuto={period?.startsWith('auto')}/>
            </> : null}
            {oneThirdTimer ? <div className="timer">
                {renderTime(displayTimeRemaining)}
                {icon ? <i className={`timerIcon fas fa-${icon}`} /> : null }
                {showHrReviewBanner ? <div className="scoreStatus">{t`Under Review`}</div> : null}
                {/*TODO don't depend on a scoreupdate existing to load this*/}
                {scoreUpdate ? <RandomizationArtifacts number={scoreUpdate.params.rand as 1 | 2 | 3} /> : null}
            </div> : null}
            {oneThirdScores && !keyPanel && !isSingleTeam ? <>
                <div className={`overlayScoreWrapper ${hflip ? 'blue' : 'red'} ${oneThirdTimer ? '' : 'wide'}`}><ScoreElement color={hflip ? 'blue' : 'red'} score={hflip ? blueScore : redScore} isAuto={!period || period.startsWith('auto')} isPlayoff={isPlayoff} /></div>
                <div className={`overlayScoreWrapper ${hflip ? 'red' : 'blue'} ${oneThirdTimer ? '' : 'wide'} right`}><ScoreElement color={hflip ? 'red' : 'blue'} score={hflip ? redScore : blueScore} isAuto={!period || period.startsWith('auto')} isPlayoff={isPlayoff} right /></div>
            </> : null}
            {oneThirdScores && (keyPanel || isSingleTeam) ? <>
                <div className={`overlayScoreWrapper ${keyPanel === 'auto' ? 'keyPanel' : null} ${isSingleTeam ? 'orange' : (hflip ? 'blue' : 'red')} ${oneThirdTimer ? '' : 'wide'}`}><Score color={isSingleTeam ? 'orange' : (hflip ? 'blue' : 'red')} score={hflip ? blueScore : redScore} isSingleTeam={isSingleTeam} isPlayoff={isPlayoff} isAuto /></div>
                <div className={`overlayScoreWrapper right ${keyPanel === 'teleop' ? 'keyPanel' : null} ${isSingleTeam ? 'orange' : (hflip ? 'red' : 'blue')} ${oneThirdTimer ? '' : 'wide'}`}><Score color={isSingleTeam ? 'orange' : (hflip ? 'red' : 'blue')} score={period?.startsWith('auto') ? {} : (hflip ? redScore : blueScore)} right isSingleTeam={isSingleTeam} isPlayoff={isPlayoff} /></div>
            </> : null}
        </OneThird>
        {audienceKey}
    </div>
}